import React, { useEffect, useCallback, useMemo } from "react";
import MainLayout from "../../../../layouts/MainLayout";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { faEdit, faTrash, faUndo } from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import ListHeader from "../../../../components/ListHeader";
import useAction from "../../../../hooks/useAction";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import useGenericFilter from "../../../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../../../components/AutoCompleteFilter";
import baseService from "../../../../services/baseService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import FilterForm from "../../../../components/FilterForm";
import TableWithInactiveFilter from "../../../../components/table/TableWithInactiveFilter";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const KitItemsPage = () => {
  const { equipmentKitId } = useParams();
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate);

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "equipments_kits_items",
  );

  // --- Callbacks para o useGenericFilter ---
  const handleFilterApply = useCallback(
    (currentLocalFilterValues) => {
      const transformed = transformFiltersForUrl(currentLocalFilterValues);
      applyUrlFilters(transformed);
    },
    [applyUrlFilters],
  );

  const handleFilterClear = useCallback(
    (initialLocalFilterStateFromHook) => {
      const transformedInitial = transformFiltersForUrl(
        initialLocalFilterStateFromHook,
      );
      applyUrlFilters(transformedInitial);
    },
    [applyUrlFilters],
  );

  // --- Gerenciamento de Filtros Locais do Formulário ---
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        name: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  // --- Lógica de busca de itens de kit com TanStack Query ---
  const {
    data: kitItemsResponseData,
    isLoading,
    isFetching,
  } = useQueryWithLoader({
    queryKey: ["equipments_kits_items", equipmentKitId, filters, pagination],
    queryFn: async () => {
      const filtersTranformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(
        entities.equipmentsKits.items.get(equipmentKitId),
        params,
      );
      return response.result;
    },
    enabled: !!equipmentKitId,
    staleTime: 60 * 1000,
    keepPreviousData: true,
    select: (data) => {
      return {
        kitItems: data.data.map((item) => ({
          id: item.id,
          name: item.name,
          isDeleted: !!item.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
  });

  const { kitItems = [], totalPages = 1 } = kitItemsResponseData || {};

  const columns = [{ key: "name", label: "Nome" }];

  // --- Função para Forçar o Refetch da Lista (invalida o cache) ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["equipments_kits_items", equipmentKitId, filters, pagination],
    });
  }, [queryClient, equipmentKitId, filters, pagination]);

  // --- Gerenciamento de Modal de Confirmação e Ações ---
  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  const inactiveKitItemMutation = useMutation({
    mutationFn: async (kitItemIdToActivate) => {
      showLoader();
      const itemToActivate = kitItems.find(
        (item) => item.id === kitItemIdToActivate,
      );
      const payload = { deleted_at: null };
      if (itemToActivate && itemToActivate.name) {
        payload.name = itemToActivate.name;
      }
      const response = await baseServiceDelete(
        entities.equipmentsKits.items.delete(
          equipmentKitId,
          kitItemIdToActivate,
        ),
        payload,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) refreshList();
    },
    onError: (err) => {
      console.error("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmation();
    },
  });

  const activateKitItemMutation = inactiveKitItemMutation;
  // --- useEffect para mensagens de estado de localização ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const actions = useMemo(
    () => [
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar item dos kits",
        onClick: (item) =>
          navigate(
            `/aluguel/kits-equipamentos/${equipmentKitId}/itens/${item.id}/editar`,
          ),
        show: (item) => !item.isDeleted && canAccess("Atualizar item dos kits"),
      },
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir item dos kits",
        onClick: (item) =>
          showConfirmation(
            item,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveKitItemMutation.mutateAsync(item.id);
              } catch (error) {
                console.error("Erro ao excluir kit:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (item) => !item.isDeleted && canAccess("Excluir item dos kits"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir item dos kits",
        onClick: (item) =>
          showConfirmation(
            item,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateKitItemMutation.mutateAsync(item.id);
              } catch (error) {
                console.error("Erro ao restaurar kit:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (item) => item.isDeleted && canAccess("Excluir item dos kits"),
      },
    ],
    [
      navigate,
      equipmentKitId,
      canAccess,
      showConfirmation,
      inactiveKitItemMutation,
      activateKitItemMutation,
    ],
  ); // Adicionado handleDelete e refreshList

  const inputsfilters = useMemo(
    () => [
      {
        label: "Nome",
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="name"
            model="equipmentKitItem"
            value={localFilterValues.name}
            onChange={(selected) => handleFilterChange("name", selected)}
            placeholder="Filtre os itens pelo nome"
            isMulti
            isClearable
          />
        ),
      },
    ],
    [localFilterValues, handleFilterChange],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Itens do Kit"
        showBackButton={true}
        backUrl={`/aluguel/kits-equipamentos`}
      />
      <div className="container-fluid p-1">
        {/* Formulário de Filtros */}
        <FilterForm
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
          filters={localFilterValues}
        />

        {/* Cabeçalho da Lista de Itens do Kit */}
        <ListHeader
          title="Lista de Itens do Kit"
          buttonText="Novo Item"
          buttonLink={`/aluguel/kits-equipamentos/${equipmentKitId}/itens/adicionar`}
          canAccess={canAccess}
          permission="Criar kits"
        />

        {/* Tabela Dinâmica de Itens do Kit */}
        <TableWithInactiveFilter
          data={kitItems}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        {/* Modal de Confirmação */}
        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default KitItemsPage;
